import {
  type TranslateFn,
  type TranslateOptions,
  useTexts as useSharedTexts,
  useTranslation as useSharedTranslation,
} from '@shared/translation';
import { useCallback, useContext } from 'react';
import { hasOwnProperty } from '@/util/object';
import { LangContext } from './context';
import type { AllTexts, InterpolationValues, Translation } from './types';

type CompatibleTranslate = TranslateFn &
  ((
    text: Translation,
    values?: InterpolationValues,
    count?: number
  ) => string | undefined);

export const useLanguage = () => {
  const context = useContext(LangContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within LangProvider');
  }

  return context;
};

const useTranslation = () => {
  const { texts, translate: sharedTranslate } =
    useSharedTranslation<AllTexts>();

  const translate = useCallback<CompatibleTranslate>(
    (
      text,
      optionsOrValues?: TranslateOptions | InterpolationValues,
      count?: number
    ) => sharedTranslate(text, toTranslateOptions(optionsOrValues, count)),
    [sharedTranslate]
  );

  return {
    texts,
    translate,
  };
};

export const useTexts = () => {
  return useSharedTexts<AllTexts>();
};

export const useTranslateEnumValues = <T extends string>(
  values: any,
  emptyValue?: string
) => {
  const { translate } = useTranslation();
  type EnumValueKey = keyof typeof values;

  return useCallback(
    (value?: T) => {
      if (!value) {
        return emptyValue;
      }

      if (hasOwnProperty(values, value.toLowerCase())) {
        return translate(
          values[value.toLowerCase() as unknown as EnumValueKey]
        );
      }
      // Support old text keys with all capital letters as enum name, e.g. `fleet.model.category.values.JAW_CRUSHER`
      if (hasOwnProperty(values, value.toUpperCase())) {
        return translate(
          values[value.toUpperCase() as unknown as EnumValueKey]
        );
      }

      return value;
    },
    [translate, values, emptyValue]
  );
};

export default useTranslation;

function toTranslateOptions(
  optionsOrValues: TranslateOptions | InterpolationValues | undefined,
  count: number | undefined
): TranslateOptions {
  if (
    optionsOrValues &&
    typeof optionsOrValues === 'object' &&
    'values' in optionsOrValues
  ) {
    return optionsOrValues as TranslateOptions;
  }

  return { values: optionsOrValues as InterpolationValues | undefined, count };
}
